import React, { useState } from 'react';
import { login, register, getWorkspaces } from '../api';
import type { User, Workspace } from '../types';
import { Button, FormField, Input } from '../components/ui';
import ConistraMark from '../components/ConistraMark';
import { track, identifyUser } from '../analytics';
import { metaTrack } from '../metaPixel';
import './LoginPage.css';

type LoginPageProps = {
  onLogin: (_user: User, _workspace: Workspace | null) => void;
  onBack?: () => void;
  onSignup?: (_planCode?: string) => void;
};

function LoginPage({ onLogin, onBack, onSignup }: LoginPageProps) {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const trimmedIdentifier = identifier.trim();
      if (isRegister) {
        const response = await register({ username, email: trimmedIdentifier, password });
        // Direct register (not the /signup onboarding funnel) — still a signup.
        identifyUser(response.user);
        track('app_signup_completed', { source: 'login_page_register' });
        // The other door into the product. Reported to Meta under the same
        // standard event as the onboarding funnel's signup, so the optimiser
        // sees one registration count rather than two half-sized ones.
        metaTrack('CompleteRegistration', {
          content_name: 'direct_register',
          status: 'no_plan',
        });
        onLogin(response.user, response.workspace || null);
      } else {
        const payload = trimmedIdentifier.includes('@')
          ? { email: trimmedIdentifier, password }
          : { username: trimmedIdentifier, password };
        const response = await login(payload);
        const workspacesData = await getWorkspaces();
        identifyUser(response.user);
        track('app_login_completed');
        onLogin(response.user, workspacesData.results?.[0] || null);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to sign in. Please check your credentials and try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsRegister((v) => !v);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-page__bg" aria-hidden="true" />

      <div className="login-page__inner">
        {/* Brand */}
        <div className="login-brand">
          {onBack && (
            <button
              type="button"
              className="login-card__switch-btn"
              style={{
                fontSize: 'var(--text-sm)',
                alignSelf: 'flex-start',
                marginBottom: 'var(--space-1)',
              }}
              onClick={onBack}
            >
              ← Back
            </button>
          )}
          <div className="login-brand__logo" aria-hidden="true">
            <ConistraMark size={64} />
          </div>
          <h1 className="login-brand__name">conistra</h1>
          <p className="login-brand__tagline">Your social media, on autopilot.</p>
        </div>

        {/* Card */}
        <div className="login-card">
          <div className="login-card__header">
            <h2 className="login-card__title">
              {isRegister ? 'Create your account' : 'Welcome back'}
            </h2>
            <p className="login-card__subtitle">
              {isRegister ? 'Get started — it only takes a minute.' : 'Sign in to your workspace.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-card__form" noValidate>
            {isRegister && (
              <FormField
                label="Username"
                htmlFor="login-username"
                hint="Letters, numbers, and underscores only."
                required
              >
                <Input
                  id="login-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="your_username"
                  autoComplete="username"
                  autoFocus
                  required
                />
              </FormField>
            )}

            <FormField
              label={isRegister ? 'Email' : 'Email or username'}
              htmlFor="login-identifier"
              required
            >
              <Input
                id="login-identifier"
                type={isRegister ? 'email' : 'text'}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={isRegister ? 'you@example.com' : 'you@example.com or your_username'}
                autoComplete={isRegister ? 'email' : 'username'}
                autoFocus={!isRegister}
                required
              />
            </FormField>

            <FormField label="Password" htmlFor="login-password" required>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                required
              />
            </FormField>

            {error && (
              <div className="login-error" role="alert">
                <span className="login-error__icon" aria-hidden="true">
                  ⚠
                </span>
                {error}
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" full loading={loading}>
              {isRegister ? 'Create account' : 'Sign in'}
            </Button>
          </form>

          <div className="login-card__footer">
            <span className="login-card__footer-text">
              {isRegister ? 'Already have an account?' : "Don't have an account yet?"}
            </span>
            {onSignup && !isRegister ? (
              <button
                type="button"
                className="login-card__switch-btn"
                onClick={() => onSignup('creator')}
              >
                Get started →
              </button>
            ) : (
              <button type="button" className="login-card__switch-btn" onClick={switchMode}>
                {isRegister ? 'Sign in' : 'Create one'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
